/**
 * Idea bank behind the public Content Calendar Generator.
 *
 * Deliberately rule-based rather than AI-generated: the tool is open to anonymous
 * visitors, so every generated calendar would otherwise cost API credits and be
 * trivially abusable.
 *
 * Each niche's usable pool = its own specific ideas + every generic template with
 * the niche's `topic` substituted in. That keeps a 7-posts-per-week calendar (28
 * slots) from repeating itself while keeping the writing maintainable.
 */

export type PillarId =
    | 'edukasi'
    | 'inspirasi'
    | 'hiburan'
    | 'promosi'
    | 'interaksi'
    | 'balik-layar'

export interface Pillar {
    id: PillarId
    /** Translation key for the chip label. */
    labelKey: string
    /** Translation key for why this pillar exists — shown in the legend so the calendar teaches, not just lists. */
    purposeKey: string
    /** Tailwind classes for the pillar chip. */
    accent: string
}

export const PILLARS: Record<PillarId, Pillar> = {
    edukasi: {
        id: 'edukasi',
        labelKey: 'contentIdeas.pillars.edukasi.label',
        purposeKey: 'contentIdeas.pillars.edukasi.purpose',
        accent: 'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300',
    },
    inspirasi: {
        id: 'inspirasi',
        labelKey: 'contentIdeas.pillars.inspirasi.label',
        purposeKey: 'contentIdeas.pillars.inspirasi.purpose',
        accent: 'bg-violet-50 text-violet-700 dark:bg-violet-900/20 dark:text-violet-300',
    },
    hiburan: {
        id: 'hiburan',
        labelKey: 'contentIdeas.pillars.hiburan.label',
        purposeKey: 'contentIdeas.pillars.hiburan.purpose',
        accent: 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300',
    },
    promosi: {
        id: 'promosi',
        labelKey: 'contentIdeas.pillars.promosi.label',
        purposeKey: 'contentIdeas.pillars.promosi.purpose',
        accent: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300',
    },
    interaksi: {
        id: 'interaksi',
        labelKey: 'contentIdeas.pillars.interaksi.label',
        purposeKey: 'contentIdeas.pillars.interaksi.purpose',
        accent: 'bg-rose-50 text-rose-700 dark:bg-rose-900/20 dark:text-rose-300',
    },
    'balik-layar': {
        id: 'balik-layar',
        labelKey: 'contentIdeas.pillars.balik-layar.label',
        purposeKey: 'contentIdeas.pillars.balik-layar.purpose',
        accent: 'bg-slate-100 text-slate-700 dark:bg-slate-700/40 dark:text-slate-300',
    },
}

/**
 * Weight of each pillar in the rotation. Roughly the 80/20 rule: only about one
 * in seven posts sells directly.
 */
export const PILLAR_ROTATION: PillarId[] = [
    'edukasi',
    'hiburan',
    'interaksi',
    'inspirasi',
    'edukasi',
    'balik-layar',
    'promosi',
]

export interface Idea {
    pillar: PillarId
    /** Translation key for the idea copy. Generic ideas interpolate {{topik}}. */
    textKey: string
}

export interface Niche {
    id: string
    /** Translation key for the niche name shown in the picker. */
    labelKey: string
    /** Translation key for the subject interpolated into generic ideas as {{topik}}. */
    topicKey: string
    ideas: Idea[]
}

/** Applies to every niche once {{topik}} is interpolated. */
const GENERIC_IDEAS: Idea[] = [
    { pillar: 'edukasi', textKey: 'contentIdeas.generic.0' },
    { pillar: 'edukasi', textKey: 'contentIdeas.generic.1' },
    { pillar: 'edukasi', textKey: 'contentIdeas.generic.2' },
    { pillar: 'edukasi', textKey: 'contentIdeas.generic.3' },
    { pillar: 'edukasi', textKey: 'contentIdeas.generic.4' },
    { pillar: 'inspirasi', textKey: 'contentIdeas.generic.5' },
    { pillar: 'inspirasi', textKey: 'contentIdeas.generic.6' },
    { pillar: 'inspirasi', textKey: 'contentIdeas.generic.7' },
    { pillar: 'inspirasi', textKey: 'contentIdeas.generic.8' },
    { pillar: 'hiburan', textKey: 'contentIdeas.generic.9' },
    { pillar: 'hiburan', textKey: 'contentIdeas.generic.10' },
    { pillar: 'hiburan', textKey: 'contentIdeas.generic.11' },
    { pillar: 'hiburan', textKey: 'contentIdeas.generic.12' },
    { pillar: 'interaksi', textKey: 'contentIdeas.generic.13' },
    { pillar: 'interaksi', textKey: 'contentIdeas.generic.14' },
    { pillar: 'interaksi', textKey: 'contentIdeas.generic.15' },
    { pillar: 'interaksi', textKey: 'contentIdeas.generic.16' },
    { pillar: 'promosi', textKey: 'contentIdeas.generic.17' },
    { pillar: 'promosi', textKey: 'contentIdeas.generic.18' },
    { pillar: 'promosi', textKey: 'contentIdeas.generic.19' },
    { pillar: 'balik-layar', textKey: 'contentIdeas.generic.20' },
    { pillar: 'balik-layar', textKey: 'contentIdeas.generic.21' },
    { pillar: 'balik-layar', textKey: 'contentIdeas.generic.22' },
    { pillar: 'balik-layar', textKey: 'contentIdeas.generic.23' },
]

export const NICHES: Niche[] = [
    {
        id: 'kuliner',
        labelKey: 'contentIdeas.niches.kuliner.label',
        topicKey: 'contentIdeas.niches.kuliner.topic',
        ideas: [
            { pillar: 'edukasi', textKey: 'contentIdeas.niches.kuliner.ideas.0' },
            { pillar: 'edukasi', textKey: 'contentIdeas.niches.kuliner.ideas.1' },
            { pillar: 'hiburan', textKey: 'contentIdeas.niches.kuliner.ideas.2' },
            { pillar: 'hiburan', textKey: 'contentIdeas.niches.kuliner.ideas.3' },
            { pillar: 'inspirasi', textKey: 'contentIdeas.niches.kuliner.ideas.4' },
            { pillar: 'promosi', textKey: 'contentIdeas.niches.kuliner.ideas.5' },
            { pillar: 'interaksi', textKey: 'contentIdeas.niches.kuliner.ideas.6' },
            { pillar: 'balik-layar', textKey: 'contentIdeas.niches.kuliner.ideas.7' },
            { pillar: 'balik-layar', textKey: 'contentIdeas.niches.kuliner.ideas.8' },
        ],
    },
    {
        id: 'fashion-kecantikan',
        labelKey: 'contentIdeas.niches.fashion-kecantikan.label',
        topicKey: 'contentIdeas.niches.fashion-kecantikan.topic',
        ideas: [
            { pillar: 'edukasi', textKey: 'contentIdeas.niches.fashion-kecantikan.ideas.0' },
            { pillar: 'edukasi', textKey: 'contentIdeas.niches.fashion-kecantikan.ideas.1' },
            { pillar: 'hiburan', textKey: 'contentIdeas.niches.fashion-kecantikan.ideas.2' },
            { pillar: 'inspirasi', textKey: 'contentIdeas.niches.fashion-kecantikan.ideas.3' },
            { pillar: 'inspirasi', textKey: 'contentIdeas.niches.fashion-kecantikan.ideas.4' },
            { pillar: 'promosi', textKey: 'contentIdeas.niches.fashion-kecantikan.ideas.5' },
            { pillar: 'interaksi', textKey: 'contentIdeas.niches.fashion-kecantikan.ideas.6' },
            { pillar: 'balik-layar', textKey: 'contentIdeas.niches.fashion-kecantikan.ideas.7' },
            { pillar: 'balik-layar', textKey: 'contentIdeas.niches.fashion-kecantikan.ideas.8' },
        ],
    },
    {
        id: 'fitness-kesehatan',
        labelKey: 'contentIdeas.niches.fitness-kesehatan.label',
        topicKey: 'contentIdeas.niches.fitness-kesehatan.topic',
        ideas: [
            { pillar: 'edukasi', textKey: 'contentIdeas.niches.fitness-kesehatan.ideas.0' },
            { pillar: 'edukasi', textKey: 'contentIdeas.niches.fitness-kesehatan.ideas.1' },
            { pillar: 'hiburan', textKey: 'contentIdeas.niches.fitness-kesehatan.ideas.2' },
            { pillar: 'inspirasi', textKey: 'contentIdeas.niches.fitness-kesehatan.ideas.3' },
            { pillar: 'inspirasi', textKey: 'contentIdeas.niches.fitness-kesehatan.ideas.4' },
            { pillar: 'promosi', textKey: 'contentIdeas.niches.fitness-kesehatan.ideas.5' },
            { pillar: 'interaksi', textKey: 'contentIdeas.niches.fitness-kesehatan.ideas.6' },
            { pillar: 'balik-layar', textKey: 'contentIdeas.niches.fitness-kesehatan.ideas.7' },
            { pillar: 'balik-layar', textKey: 'contentIdeas.niches.fitness-kesehatan.ideas.8' },
        ],
    },
    {
        id: 'bisnis-keuangan',
        labelKey: 'contentIdeas.niches.bisnis-keuangan.label',
        topicKey: 'contentIdeas.niches.bisnis-keuangan.topic',
        ideas: [
            { pillar: 'edukasi', textKey: 'contentIdeas.niches.bisnis-keuangan.ideas.0' },
            { pillar: 'edukasi', textKey: 'contentIdeas.niches.bisnis-keuangan.ideas.1' },
            { pillar: 'hiburan', textKey: 'contentIdeas.niches.bisnis-keuangan.ideas.2' },
            { pillar: 'inspirasi', textKey: 'contentIdeas.niches.bisnis-keuangan.ideas.3' },
            { pillar: 'inspirasi', textKey: 'contentIdeas.niches.bisnis-keuangan.ideas.4' },
            { pillar: 'promosi', textKey: 'contentIdeas.niches.bisnis-keuangan.ideas.5' },
            { pillar: 'interaksi', textKey: 'contentIdeas.niches.bisnis-keuangan.ideas.6' },
            { pillar: 'balik-layar', textKey: 'contentIdeas.niches.bisnis-keuangan.ideas.7' },
            { pillar: 'balik-layar', textKey: 'contentIdeas.niches.bisnis-keuangan.ideas.8' },
        ],
    },
    {
        id: 'edukasi-kursus',
        labelKey: 'contentIdeas.niches.edukasi-kursus.label',
        topicKey: 'contentIdeas.niches.edukasi-kursus.topic',
        ideas: [
            { pillar: 'edukasi', textKey: 'contentIdeas.niches.edukasi-kursus.ideas.0' },
            { pillar: 'edukasi', textKey: 'contentIdeas.niches.edukasi-kursus.ideas.1' },
            { pillar: 'hiburan', textKey: 'contentIdeas.niches.edukasi-kursus.ideas.2' },
            { pillar: 'inspirasi', textKey: 'contentIdeas.niches.edukasi-kursus.ideas.3' },
            { pillar: 'inspirasi', textKey: 'contentIdeas.niches.edukasi-kursus.ideas.4' },
            { pillar: 'promosi', textKey: 'contentIdeas.niches.edukasi-kursus.ideas.5' },
            { pillar: 'interaksi', textKey: 'contentIdeas.niches.edukasi-kursus.ideas.6' },
            { pillar: 'balik-layar', textKey: 'contentIdeas.niches.edukasi-kursus.ideas.7' },
            { pillar: 'balik-layar', textKey: 'contentIdeas.niches.edukasi-kursus.ideas.8' },
        ],
    },
    {
        id: 'jasa-kreatif',
        labelKey: 'contentIdeas.niches.jasa-kreatif.label',
        topicKey: 'contentIdeas.niches.jasa-kreatif.topic',
        ideas: [
            { pillar: 'edukasi', textKey: 'contentIdeas.niches.jasa-kreatif.ideas.0' },
            { pillar: 'edukasi', textKey: 'contentIdeas.niches.jasa-kreatif.ideas.1' },
            { pillar: 'hiburan', textKey: 'contentIdeas.niches.jasa-kreatif.ideas.2' },
            { pillar: 'inspirasi', textKey: 'contentIdeas.niches.jasa-kreatif.ideas.3' },
            { pillar: 'inspirasi', textKey: 'contentIdeas.niches.jasa-kreatif.ideas.4' },
            { pillar: 'promosi', textKey: 'contentIdeas.niches.jasa-kreatif.ideas.5' },
            { pillar: 'interaksi', textKey: 'contentIdeas.niches.jasa-kreatif.ideas.6' },
            { pillar: 'balik-layar', textKey: 'contentIdeas.niches.jasa-kreatif.ideas.7' },
            { pillar: 'balik-layar', textKey: 'contentIdeas.niches.jasa-kreatif.ideas.8' },
        ],
    },
    {
        id: 'umum',
        labelKey: 'contentIdeas.niches.umum.label',
        topicKey: 'contentIdeas.niches.umum.topic',
        ideas: [
            { pillar: 'edukasi', textKey: 'contentIdeas.niches.umum.ideas.0' },
            { pillar: 'inspirasi', textKey: 'contentIdeas.niches.umum.ideas.1' },
            { pillar: 'hiburan', textKey: 'contentIdeas.niches.umum.ideas.2' },
            { pillar: 'promosi', textKey: 'contentIdeas.niches.umum.ideas.3' },
            { pillar: 'interaksi', textKey: 'contentIdeas.niches.umum.ideas.4' },
            { pillar: 'balik-layar', textKey: 'contentIdeas.niches.umum.ideas.5' },
        ],
    },
]

/**
 * Full idea pool for a niche: its own ideas plus every generic template.
 *
 * Substitution moved into the caller's t() call — the pool now carries keys, and
 * only the renderer knows the active language, so {{topik}} is interpolated there.
 */
export function buildIdeaPool(niche: Niche): Idea[] {
    return [...niche.ideas, ...GENERIC_IDEAS]
}
